import React from 'react';

import {
  ReferenceArrayInput,
  SelectArrayInput,
} from 'react-admin';
import { DateTime } from 'luxon';

import { Box } from '@mui/material';
import { capitalize } from 'lodash';

import entityTypes from '../../../../constants/entityTypes';
import SmartDateTimeInput from '../../../../customFields/SmartDateTimeInput';
import ReferenceEntitiesInput from '../../../../customFields/ReferenceEntitiesInput';
import dateParse, { dateFormat } from '../../../utils/dateParser';
import RuleSelector from '../../../utils/RuleSelector';
import FieldBox from '../FieldBox';
import RangeFields from '../RangeFields';

const isPastDate = (value: string) => {
  if (DateTime.fromISO(value) > DateTime.now()) return 'Invalid date';
  return undefined;
};

const dateRangeValidation = (
  _value: string,
  allValues: { report: {
    entitiesFrom?: string;
    entitiesTo?: string;
    screeningFrom?: string;
    screeningTo?: string;
  }; },
) => {
  const {
    entitiesFrom,
    entitiesTo,
    screeningFrom,
    screeningTo,
  } = allValues.report ?? {};
  if (entitiesFrom && entitiesTo && DateTime.fromISO(entitiesFrom) > DateTime.fromISO(entitiesTo)) {
    return 'entities from should be before entities to';
  }
  if (
    screeningFrom
    && screeningTo
    && DateTime.fromISO(screeningFrom) > DateTime.fromISO(screeningTo)
  ) {
    return 'screening from should be before screening to';
  }
  return undefined;
};

type EntityIdOrObject = { id?: string } | string;

type PortfolioScreeningFormData = {
  report?: {
    entityIds?: EntityIdOrObject[];
  };
};

const isPortfolioScreeningFormData = (data: unknown): data is PortfolioScreeningFormData => {
  if (typeof data !== 'object' || data === null) return false;

  const { report } = data as PortfolioScreeningFormData;
  if (report === undefined) return true;
  if (typeof report !== 'object' || report === null) return false;
  if (report.entityIds === undefined) return true;

  return Array.isArray(report.entityIds);
};

export const portfolioScreeningTransform = (data: unknown) => {
  if (!isPortfolioScreeningFormData(data)) return data;

  return {
    ...data,
    report: {
      ...data.report,
      entityIds: data.report?.entityIds
        ?.map((entity) => (typeof entity === 'string' ? entity : entity?.id))
        .filter((value): value is string => Boolean(value)),
    },
  };
};

const PortfolioScreening = () => (
  <Box display="flex" flexDirection="column" gap={4}>
    <FieldBox title="Entity types" description="Filter to only include Business and/or Individual entities. Leave empty to include both.">
      <SelectArrayInput
        choices={[
          entityTypes.BUSINESS,
          entityTypes.INDIVIDUAL,
        ].map((type) => ({ id: type, name: capitalize(type) }))}
        fullWidth
        label=""
        source="report.entityTypes"
        helperText={false}
      />
    </FieldBox>
    <FieldBox title="Entities" description="Only include these specific entities. Leave empty to include all matching entities.">
      <ReferenceEntitiesInput
        source="report.entityIds"
        label=""
        helperText={false}
        allowedTypes={[entityTypes.BUSINESS, entityTypes.INDIVIDUAL]}
      />
    </FieldBox>
    <FieldBox title="Groups" description="Only include entities assigned to all selected groups. Leave empty to include entities regardless of groups.">
      <ReferenceArrayInput perPage={100} source="report.groupIds" reference="groups" filter={{ active: true }}>
        <SelectArrayInput fullWidth label="" helperText={false} />
      </ReferenceArrayInput>
    </FieldBox>
    <FieldBox title="Entity created date range" description="Only include entities created within this range (CET timezone).">
      <RangeFields
        from={(
          <SmartDateTimeInput
            fullWidth
            source="report.entitiesFrom"
            label="From"
            validate={[isPastDate, dateRangeValidation]}
            parse={dateParse}
            format={dateFormat}
            helperText={false}
          />
        )}
        to={(
          <SmartDateTimeInput
            fullWidth
            source="report.entitiesTo"
            label="To"
            validate={[isPastDate, dateRangeValidation]}
            parse={dateParse}
            format={dateFormat}
            helperText={false}
          />
        )}
      />
    </FieldBox>
    <FieldBox title="Screening date range" description="Only include screening data collected within this range (CET timezone).">
      <RangeFields
        from={(
          <SmartDateTimeInput
            fullWidth
            source="report.screeningFrom"
            label="From"
            validate={[isPastDate, dateRangeValidation]}
            parse={dateParse}
            format={dateFormat}
            helperText={false}
          />
        )}
        to={(
          <SmartDateTimeInput
            fullWidth
            source="report.screeningTo"
            label="To"
            validate={[isPastDate, dateRangeValidation]}
            parse={dateParse}
            format={dateFormat}
            helperText={false}
          />
        )}
      />
    </FieldBox>
    <FieldBox title="Rules" description="For each selected origination rule ID, include latest execution timestamps per entity. Leave empty to exclude rule execution columns.">
      <RuleSelector source="report.originationRuleIds" label="Rules" field="id" helperText={false} />
    </FieldBox>
  </Box>
);

export default PortfolioScreening;
